import { useId } from "react";
import {
  BriefcaseBusiness,
  Building2,
  CarFront,
  Check,
  GraduationCap,
  Heart,
  House,
  Landmark,
  TrendingUp,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import {
  returnProfileCards,
  type ReturnProfile,
} from "../return-builder-model";

export interface ReturnProfileCardsProps {
  value: ReturnProfile;
  disabled?: boolean;
  showHeading?: boolean;
  onChange: (profile: ReturnProfile) => void;
}

const profileIcons: Record<string, LucideIcon> = {
  employee: BriefcaseBusiness,
  business: Building2,
  gig: CarFront,
  investments: TrendingUp,
  homeowner: House,
  dependents: UsersRound,
  education: GraduationCap,
  charity: Heart,
  rental: Building2,
  retirement: Landmark,
};

/** A controlled navigation preference; selecting an area never supplies a tax answer. */
export function ReturnProfileCards({
  value,
  disabled = false,
  showHeading = true,
  onChange,
}: ReturnProfileCardsProps) {
  const id = useId();
  const selectedCount = returnProfileCards.filter((card) =>
    value.selected.includes(card.id),
  ).length;

  return (
    <section className="rb-profile" aria-labelledby={id + "-title"}>
      <div className="rb-profile-heading">
        {showHeading && <span className="rb-section-tag">Return profile</span>}
        <h2 id={id + "-title"} className={showHeading ? undefined : "sr-only"}>
          Tell us about this return.
        </h2>
        <p id={id + "-help"}>
          Select all that apply. We’ll organize the next sections around your
          client’s return.
        </p>
      </div>
      <div
        className="rb-profile-grid"
        role="group"
        aria-describedby={id + "-help"}
        aria-label="Areas of this return"
      >
        {returnProfileCards.map((card) => {
          const selected = value.selected.includes(card.id);
          const Icon = profileIcons[card.id] ?? BriefcaseBusiness;
          const descriptionId = id + "-" + card.id + "-description";
          return (
            <button
              key={card.id}
              type="button"
              className={"rb-profile-card" + (selected ? " is-selected" : "")}
              aria-label={card.label}
              aria-describedby={descriptionId}
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                onChange({
                  ...value,
                  selected: selected
                    ? value.selected.filter((entry) => entry !== card.id)
                    : [...value.selected, card.id],
                  confirmed: false,
                });
              }}
            >
              <span className="rb-profile-icon" aria-hidden="true">
                <Icon size={27} strokeWidth={1.65} aria-hidden="true" />
              </span>
              <span className="rb-profile-card-copy">
                <span className="rb-profile-card-title">{card.label}</span>
                <span
                  className="rb-profile-card-description"
                  id={descriptionId}
                >
                  {card.description}
                </span>
              </span>
              {selected && (
                <span className="rb-profile-card-state" aria-hidden="true">
                  <Check size={15} strokeWidth={2.3} aria-hidden="true" />
                  Selected
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p
        className="rb-profile-summary"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {selectedCount === 0
          ? "No areas selected yet."
          : `${selectedCount} ${selectedCount === 1 ? "area" : "areas"} selected.`}{" "}
        You can update these selections as you prepare the return.
      </p>
    </section>
  );
}
